import React from "react";

import Button, { ButtonTypes } from "../../../../components/Button";
import {
  Card,
  CardHeader,
  CardTitle,
  DefinitionList,
  Pill,
  Tone,
} from "../../styles";
import { useQueueDraft } from "../hooks/useQueueDraft";
import { ActionButtons, ActionError, ActionNote, ReviewCheck } from "../styles";
import { CorporateActionDetailProps, OpenActionItem } from "../types";
import { effectiveDateOf, ratioTextOf, typeLabelOf } from "../utils/toWorkItem";
import { CorporateActionPreview } from "./CorporateActionPreview";

const statusPillOf = ({
  status,
  action: { isEffective },
}: OpenActionItem): { label: string; tone: Tone } => {
  if (status === "deferred") {
    return { label: "Deferred", tone: "unknown" };
  }
  return isEffective
    ? { label: "Pending review", tone: "warning" }
    : { label: "Not yet effective", tone: "unknown" };
};

export const CorporateActionDetail: React.FC<CorporateActionDetailProps> = ({
  item,
  isSubmitting,
  error,
  onDefer,
  onResume,
  onReconcile,
}) => {
  const { action, status, symbol } = item;
  const [draft, updateDraft] = useQueueDraft("corporate", item.key);
  const isReviewed = draft?.isReviewed ?? false;
  const isDeferred = status === "deferred";
  const typeLabel = typeLabelOf(action);
  const effectiveDate = effectiveDateOf(action);
  const ratio = ratioTextOf(action);
  const pill = statusPillOf(item);

  const handleReviewedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked;
    updateDraft(() => (isChecked ? { isReviewed: true } : null));
  };

  return (
    <Card aria-label={`Review ${symbol}`}>
      <CardHeader>
        <CardTitle>{`${symbol} · ${typeLabel}`}</CardTitle>
        <Pill $tone={pill.tone}>{pill.label}</Pill>
      </CardHeader>
      <DefinitionList>
        <dt>Instrument</dt>
        <dd>{`${action.name} · ${action.exchange}`}</dd>
        <dt>Effective</dt>
        <dd>{effectiveDate}</dd>
        {ratio && (
          <>
            <dt>Ratio</dt>
            <dd>{ratio}</dd>
          </>
        )}
        {action.successorSymbol && (
          <>
            <dt>Successor</dt>
            <dd>{action.successorSymbol}</dd>
          </>
        )}
        <dt>Open holdings</dt>
        <dd>{action.preview.openHoldings}</dd>
        <dt>Affected users</dt>
        <dd>{action.preview.affectedUsers}</dd>
      </DefinitionList>
      <CorporateActionPreview action={action} />
      <ActionNote>
        Reconciling confirms this catalogue entry. It doesn't change any holding
        or clear a holder's block; each owner still corrects their own position
        where one is needed.
      </ActionNote>
      {action.isEffective ? (
        <ReviewCheck>
          <input
            type="checkbox"
            checked={isReviewed}
            disabled={isSubmitting}
            onChange={handleReviewedChange}
          />
          I have reviewed this action and its effective date.
        </ReviewCheck>
      ) : (
        <ActionNote>
          {`This action takes effect on ${effectiveDate}. It can be reconciled from that date.`}
        </ActionNote>
      )}
      {error && <ActionError role="alert">{error}</ActionError>}
      <ActionButtons>
        <Button
          type={ButtonTypes.secondary}
          disabled={isSubmitting}
          onClick={isDeferred ? onResume : onDefer}
        >
          {isDeferred ? "Return to pending" : "Defer action"}
        </Button>
        <Button
          type={ButtonTypes.primary}
          disabled={isSubmitting || !action.isEffective || !isReviewed}
          onClick={onReconcile}
        >
          Reconcile action
        </Button>
      </ActionButtons>
    </Card>
  );
};
